import React, { useState, useEffect } from 'react';
import {
    Button,
    Card,
    CardContent,
    TextField,
    Typography,
    CircularProgress,
    Grid,
    Alert,
} from '@mui/material';
import SearchableSelect from '../SearchableSelect';
import { TIPOS_MOVIMIENTO_STOCK } from '../../constants/tiposMovimientoStock';
import type {
    ConsultaAnalizarRequest,
    ModuloConsultaIa,
    SubModuloInventarioIa,
    SubModuloTesoreriaIa,
} from '../../types/consultasIa';

export interface ConsultaChatProps {
    titulo: string;
    modulo: ModuloConsultaIa;
    onConsulta: (consulta: ConsultaAnalizarRequest) => Promise<string>;
}

const parseOptionalInt = (s: string): number | undefined => {
    const t = s.trim();
    if (!t) return undefined;
    const n = parseInt(t, 10);
    return Number.isFinite(n) ? n : undefined;
};

const ConsultaChat: React.FC<ConsultaChatProps> = ({ titulo, modulo, onConsulta }) => {
    const [pregunta, setPregunta] = useState('');
    const [fechaDesde, setFechaDesde] = useState('');
    const [fechaHasta, setFechaHasta] = useState('');
    const [loading, setLoading] = useState(false);
    const [respuesta, setRespuesta] = useState('');
    const [errorFechas, setErrorFechas] = useState('');
    const [submitError, setSubmitError] = useState('');

    const [subModuloInventario, setSubModuloInventario] = useState<SubModuloInventarioIa>('ajustes');
    const [subModuloTesoreria, setSubModuloTesoreria] = useState<SubModuloTesoreriaIa>('planilla-caja');

    const [nroCliente, setNroCliente] = useState('');
    const [nroVendedor, setNroVendedor] = useState('');
    const [asentado, setAsentado] = useState('');
    const [nroProveedor, setNroProveedor] = useState('');
    const [searchTerm, setSearchTerm] = useState('');

    const [codigoArticulo, setCodigoArticulo] = useState('');
    const [tipoMovimientoStock, setTipoMovimientoStock] = useState('');
    const [depositoStr, setDepositoStr] = useState('');

    const [tipoMovimientoCaja, setTipoMovimientoCaja] = useState('');
    const [medioCobro, setMedioCobro] = useState('');
    const [codigoCajaStr, setCodigoCajaStr] = useState('');

    const [tipoMovimientoBancario, setTipoMovimientoBancario] = useState('');
    const [tipoMovimientoVario, setTipoMovimientoVario] = useState('');
    const [nroSucStr, setNroSucStr] = useState('');

    const [codigoCajaOrigenStr, setCodigoCajaOrigenStr] = useState('');
    const [codigoCajaDestinoStr, setCodigoCajaDestinoStr] = useState('');

    useEffect(() => {
        const hoy = new Date();
        const tresMesesAtras = new Date();
        tresMesesAtras.setMonth(hoy.getMonth() - 3);
        const formatearFecha = (fecha: Date) => fecha.toISOString().split('T')[0];
        setFechaDesde(formatearFecha(tresMesesAtras));
        setFechaHasta(formatearFecha(hoy));
    }, []);

    const validarRangoFechas = (desde: string, hasta: string): boolean => {
        if (!desde || !hasta) return true;
        const fechaInicio = new Date(desde);
        const fechaFin = new Date(hasta);
        const diffMeses =
            (fechaFin.getFullYear() - fechaInicio.getFullYear()) * 12 +
            (fechaFin.getMonth() - fechaInicio.getMonth());
        if (diffMeses > 3) {
            setErrorFechas('El rango de fechas no puede ser mayor a 3 meses');
            return false;
        }
        setErrorFechas('');
        return true;
    };

    const handleFechaDesdeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const v = e.target.value;
        setFechaDesde(v);
        validarRangoFechas(v, fechaHasta);
    };

    const handleFechaHastaChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const v = e.target.value;
        setFechaHasta(v);
        validarRangoFechas(fechaDesde, v);
    };

    const buildPayload = (): ConsultaAnalizarRequest => {
        const base: ConsultaAnalizarRequest = {
            pregunta,
            modulo,
            fechaDesde: fechaDesde || undefined,
            fechaHasta: fechaHasta || undefined,
        };

        if (modulo === 'ventas') {
            return {
                ...base,
                nroCliente: nroCliente.trim() || undefined,
                nroVendedor: nroVendedor.trim() || undefined,
                asentado: asentado.trim() || undefined,
            };
        }

        if (modulo === 'compras') {
            return {
                ...base,
                nroProveedor: nroProveedor.trim() || undefined,
                asentado: asentado.trim() || undefined,
                searchTerm: searchTerm.trim() || undefined,
            };
        }

        if (modulo === 'inventario') {
            if (subModuloInventario === 'ajustes') {
                return {
                    ...base,
                    subModuloInventario: 'ajustes',
                    searchTerm: searchTerm.trim() || undefined,
                };
            }
            return {
                ...base,
                subModuloInventario: 'movimientos-stock',
                codigoArticulo: codigoArticulo.trim() || undefined,
                tipoMovimientoStock: tipoMovimientoStock || undefined,
                deposito: parseOptionalInt(depositoStr),
            };
        }

        const tes: ConsultaAnalizarRequest = { ...base, subModuloTesoreria: subModuloTesoreria };

        if (subModuloTesoreria === 'planilla-caja') {
            return {
                ...tes,
                searchTerm: searchTerm.trim() || undefined,
                codigoCaja: parseOptionalInt(codigoCajaStr),
                tipoMovimientoCaja: tipoMovimientoCaja || undefined,
                medioCobro: medioCobro.trim() || undefined,
            };
        }
        if (subModuloTesoreria === 'bancarios') {
            return {
                ...tes,
                tipoMovimientoBancario: tipoMovimientoBancario || undefined,
                codigoCaja: parseOptionalInt(codigoCajaStr),
                nroSuc: parseOptionalInt(nroSucStr),
            };
        }
        if (subModuloTesoreria === 'varios') {
            return {
                ...tes,
                tipoMovimientoVario: tipoMovimientoVario || undefined,
                codigoCaja: parseOptionalInt(codigoCajaStr),
                nroSuc: parseOptionalInt(nroSucStr),
            };
        }
        return {
            ...tes,
            codigoCajaOrigen: parseOptionalInt(codigoCajaOrigenStr),
            codigoCajaDestino: parseOptionalInt(codigoCajaDestinoStr),
            nroSuc: parseOptionalInt(nroSucStr),
        };
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setSubmitError('');
        if (!validarRangoFechas(fechaDesde, fechaHasta)) return;

        setLoading(true);
        try {
            const payload = buildPayload();
            const r = await onConsulta(payload);
            setRespuesta(r);
        } catch (error: unknown) {
            console.error('Error al procesar la consulta:', error);
            const msg = error instanceof Error ? error.message : 'Error al procesar la consulta.';
            setSubmitError(msg);
            setRespuesta('');
        } finally {
            setLoading(false);
        }
    };

    const textFieldSx = {
        '& .MuiOutlinedInput-root': {
            borderRadius: 2,
            bgcolor: '#fafafa',
            '&:hover': { bgcolor: '#f5f5f5' },
            '&.Mui-focused': { bgcolor: '#ffffff' },
        },
    };

    return (
        <Card sx={{ bgcolor: '#ffffff', borderRadius: 3, boxShadow: '0 2px 8px rgba(0,0,0,0.1)', p: 3 }}>
            <Typography variant="h4" gutterBottom sx={{ fontWeight: 600, color: '#1976d2', mb: 2 }}>
                {titulo}
            </Typography>
            <Alert severity="info" sx={{ mb: 2, borderRadius: 2 }}>
                La respuesta se basa en una sola página de datos según los filtros; ajustá fechas o filtros para acotar el
                conjunto.
            </Alert>
            <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                    <Card sx={{ borderRadius: 2, boxShadow: 'none', border: '1px solid #e0e0e0', p: 3 }}>
                        <form onSubmit={handleSubmit}>
                            <Grid container spacing={2}>
                                {modulo === 'inventario' && (
                                    <Grid item xs={12}>
                                        <SearchableSelect<SubModuloInventarioIa>
                                            label="Alcance inventario"
                                            size="small"
                                            value={subModuloInventario}
                                            onChange={(v) =>
                                                setSubModuloInventario((v ?? 'ajustes') as SubModuloInventarioIa)
                                            }
                                            options={[
                                                { value: 'ajustes', label: 'Ajustes y transferencias (formularios)' },
                                                { value: 'movimientos-stock', label: 'Movimientos de stock' },
                                            ]}
                                            sx={textFieldSx}
                                        />
                                    </Grid>
                                )}

                                {modulo === 'tesoreria' && (
                                    <Grid item xs={12}>
                                        <SearchableSelect<SubModuloTesoreriaIa>
                                            label="Alcance tesorería"
                                            size="small"
                                            value={subModuloTesoreria}
                                            onChange={(v) =>
                                                setSubModuloTesoreria((v ?? 'planilla-caja') as SubModuloTesoreriaIa)
                                            }
                                            options={[
                                                { value: 'planilla-caja', label: 'Planilla de caja' },
                                                { value: 'bancarios', label: 'Movimientos bancarios' },
                                                { value: 'varios', label: 'Movimientos varios' },
                                                { value: 'transferencias', label: 'Transferencias entre cajas' },
                                            ]}
                                            sx={textFieldSx}
                                        />
                                    </Grid>
                                )}

                                <Grid item xs={12}>
                                    <TextField
                                        fullWidth
                                        label="Pregunta"
                                        multiline
                                        rows={4}
                                        value={pregunta}
                                        onChange={(e) => setPregunta(e.target.value)}
                                        required
                                        sx={textFieldSx}
                                    />
                                </Grid>

                                {submitError && (
                                    <Grid item xs={12}>
                                        <Alert severity="error">{submitError}</Alert>
                                    </Grid>
                                )}
                                {errorFechas && (
                                    <Grid item xs={12}>
                                        <Alert severity="error" sx={{ borderRadius: 2 }}>
                                            {errorFechas}
                                        </Alert>
                                    </Grid>
                                )}

                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        label="Fecha Desde"
                                        type="date"
                                        value={fechaDesde}
                                        onChange={handleFechaDesdeChange}
                                        InputLabelProps={{ shrink: true }}
                                        sx={textFieldSx}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        label="Fecha Hasta"
                                        type="date"
                                        value={fechaHasta}
                                        onChange={handleFechaHastaChange}
                                        InputLabelProps={{ shrink: true }}
                                        sx={textFieldSx}
                                    />
                                </Grid>

                                {modulo === 'ventas' && (
                                    <>
                                        <Grid item xs={12} sm={4}>
                                            <TextField
                                                fullWidth
                                                label="Nro. Cliente"
                                                value={nroCliente}
                                                onChange={(e) => setNroCliente(e.target.value)}
                                                sx={textFieldSx}
                                            />
                                        </Grid>
                                        <Grid item xs={12} sm={4}>
                                            <TextField
                                                fullWidth
                                                label="Nro. Vendedor"
                                                value={nroVendedor}
                                                onChange={(e) => setNroVendedor(e.target.value)}
                                                sx={textFieldSx}
                                            />
                                        </Grid>
                                        <Grid item xs={12} sm={4}>
                                            <TextField
                                                fullWidth
                                                label="Asentado"
                                                value={asentado}
                                                onChange={(e) => setAsentado(e.target.value)}
                                                sx={textFieldSx}
                                            />
                                        </Grid>
                                    </>
                                )}

                                {modulo === 'compras' && (
                                    <>
                                        <Grid item xs={12} sm={4}>
                                            <TextField
                                                fullWidth
                                                label="Nro. Proveedor"
                                                value={nroProveedor}
                                                onChange={(e) => setNroProveedor(e.target.value)}
                                                sx={textFieldSx}
                                            />
                                        </Grid>
                                        <Grid item xs={12} sm={4}>
                                            <TextField
                                                fullWidth
                                                label="Asentado"
                                                value={asentado}
                                                onChange={(e) => setAsentado(e.target.value)}
                                                sx={textFieldSx}
                                            />
                                        </Grid>
                                        <Grid item xs={12} sm={4}>
                                            <TextField
                                                fullWidth
                                                label="Búsqueda"
                                                value={searchTerm}
                                                onChange={(e) => setSearchTerm(e.target.value)}
                                                helperText="Nombre, nro. comprobante..."
                                                sx={textFieldSx}
                                            />
                                        </Grid>
                                    </>
                                )}

                                {modulo === 'inventario' && subModuloInventario === 'ajustes' && (
                                    <Grid item xs={12}>
                                        <TextField
                                            fullWidth
                                            label="Búsqueda"
                                            value={searchTerm}
                                            onChange={(e) => setSearchTerm(e.target.value)}
                                            helperText="Usuario, nombre, CUIT, nro. formulario..."
                                            sx={textFieldSx}
                                        />
                                    </Grid>
                                )}

                                {modulo === 'inventario' && subModuloInventario === 'movimientos-stock' && (
                                    <>
                                        <Grid item xs={12} sm={6}>
                                            <TextField
                                                fullWidth
                                                label="Código artículo"
                                                value={codigoArticulo}
                                                onChange={(e) => setCodigoArticulo(e.target.value)}
                                                sx={textFieldSx}
                                            />
                                        </Grid>
                                        <Grid item xs={12} sm={6}>
                                            <SearchableSelect
                                                label="Tipo movimiento"
                                                size="small"
                                                allowEmpty
                                                emptyOptionLabel="Todos"
                                                value={tipoMovimientoStock || null}
                                                onChange={(v) => setTipoMovimientoStock(v ?? '')}
                                                options={Object.entries(TIPOS_MOVIMIENTO_STOCK).map(([k, label]) => ({
                                                    value: k,
                                                    label: `${k} — ${label}`,
                                                }))}
                                                sx={textFieldSx}
                                            />
                                        </Grid>
                                        <Grid item xs={12} sm={6}>
                                            <TextField
                                                fullWidth
                                                label="Nro. depósito"
                                                value={depositoStr}
                                                onChange={(e) => setDepositoStr(e.target.value)}
                                                sx={textFieldSx}
                                            />
                                        </Grid>
                                    </>
                                )}

                                {modulo === 'tesoreria' && subModuloTesoreria === 'planilla-caja' && (
                                    <>
                                        <Grid item xs={12} sm={6}>
                                            <TextField
                                                fullWidth
                                                label="Búsqueda"
                                                value={searchTerm}
                                                onChange={(e) => setSearchTerm(e.target.value)}
                                                sx={textFieldSx}
                                            />
                                        </Grid>
                                        <Grid item xs={12} sm={6}>
                                            <TextField
                                                fullWidth
                                                label="Código caja"
                                                value={codigoCajaStr}
                                                onChange={(e) => setCodigoCajaStr(e.target.value)}
                                                sx={textFieldSx}
                                            />
                                        </Grid>
                                        <Grid item xs={12} sm={6}>
                                            <SearchableSelect
                                                label="Tipo (caja)"
                                                size="small"
                                                allowEmpty
                                                emptyOptionLabel="Todos"
                                                value={tipoMovimientoCaja || null}
                                                onChange={(v) => setTipoMovimientoCaja(v ?? '')}
                                                options={[
                                                    { value: 'I', label: 'Ingreso (I)' },
                                                    { value: 'E', label: 'Egreso (E)' },
                                                ]}
                                                sx={textFieldSx}
                                            />
                                        </Grid>
                                        <Grid item xs={12} sm={6}>
                                            <TextField
                                                fullWidth
                                                label="Medio cobro"
                                                value={medioCobro}
                                                onChange={(e) => setMedioCobro(e.target.value)}
                                                sx={textFieldSx}
                                            />
                                        </Grid>
                                    </>
                                )}

                                {modulo === 'tesoreria' && subModuloTesoreria === 'bancarios' && (
                                    <>
                                        <Grid item xs={12} sm={4}>
                                            <SearchableSelect
                                                label="Tipo bancario"
                                                size="small"
                                                allowEmpty
                                                emptyOptionLabel="55 y 56"
                                                value={tipoMovimientoBancario || null}
                                                onChange={(v) => setTipoMovimientoBancario(v ?? '')}
                                                options={[
                                                    { value: '55', label: '55 Depósito' },
                                                    { value: '56', label: '56 Extracción' },
                                                ]}
                                                sx={textFieldSx}
                                            />
                                        </Grid>
                                        <Grid item xs={12} sm={4}>
                                            <TextField
                                                fullWidth
                                                label="Código caja"
                                                value={codigoCajaStr}
                                                onChange={(e) => setCodigoCajaStr(e.target.value)}
                                                sx={textFieldSx}
                                            />
                                        </Grid>
                                        <Grid item xs={12} sm={4}>
                                            <TextField
                                                fullWidth
                                                label="Nro. sucursal"
                                                value={nroSucStr}
                                                onChange={(e) => setNroSucStr(e.target.value)}
                                                sx={textFieldSx}
                                            />
                                        </Grid>
                                    </>
                                )}

                                {modulo === 'tesoreria' && subModuloTesoreria === 'varios' && (
                                    <>
                                        <Grid item xs={12} sm={4}>
                                            <SearchableSelect
                                                label="Tipo varios"
                                                size="small"
                                                allowEmpty
                                                emptyOptionLabel="5 y 7"
                                                value={tipoMovimientoVario || null}
                                                onChange={(v) => setTipoMovimientoVario(v ?? '')}
                                                options={[
                                                    { value: '5', label: '5 Ingreso VS' },
                                                    { value: '7', label: '7 Egreso VS' },
                                                ]}
                                                sx={textFieldSx}
                                            />
                                        </Grid>
                                        <Grid item xs={12} sm={4}>
                                            <TextField
                                                fullWidth
                                                label="Código caja"
                                                value={codigoCajaStr}
                                                onChange={(e) => setCodigoCajaStr(e.target.value)}
                                                sx={textFieldSx}
                                            />
                                        </Grid>
                                        <Grid item xs={12} sm={4}>
                                            <TextField
                                                fullWidth
                                                label="Nro. sucursal"
                                                value={nroSucStr}
                                                onChange={(e) => setNroSucStr(e.target.value)}
                                                sx={textFieldSx}
                                            />
                                        </Grid>
                                    </>
                                )}

                                {modulo === 'tesoreria' && subModuloTesoreria === 'transferencias' && (
                                    <>
                                        <Grid item xs={12} sm={4}>
                                            <TextField
                                                fullWidth
                                                label="Caja origen"
                                                value={codigoCajaOrigenStr}
                                                onChange={(e) => setCodigoCajaOrigenStr(e.target.value)}
                                                sx={textFieldSx}
                                            />
                                        </Grid>
                                        <Grid item xs={12} sm={4}>
                                            <TextField
                                                fullWidth
                                                label="Caja destino"
                                                value={codigoCajaDestinoStr}
                                                onChange={(e) => setCodigoCajaDestinoStr(e.target.value)}
                                                sx={textFieldSx}
                                            />
                                        </Grid>
                                        <Grid item xs={12} sm={4}>
                                            <TextField
                                                fullWidth
                                                label="Nro. sucursal"
                                                value={nroSucStr}
                                                onChange={(e) => setNroSucStr(e.target.value)}
                                                sx={textFieldSx}
                                            />
                                        </Grid>
                                    </>
                                )}

                                <Grid item xs={12}>
                                    <Button
                                        type="submit"
                                        variant="contained"
                                        color="primary"
                                        fullWidth
                                        disabled={loading || !pregunta || !!errorFechas}
                                        sx={{
                                            borderRadius: 2,
                                            px: 4,
                                            py: 1.5,
                                            textTransform: 'none',
                                            fontWeight: 500,
                                            boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                                        }}
                                    >
                                        {loading ? <CircularProgress size={24} color="inherit" /> : 'Consultar'}
                                    </Button>
                                </Grid>
                            </Grid>
                        </form>
                    </Card>
                </Grid>
                <Grid item xs={12} md={6}>
                    <Card sx={{ borderRadius: 2, boxShadow: 'none', border: '1px solid #e0e0e0' }}>
                        <CardContent>
                            <Typography
                                variant="h6"
                                gutterBottom
                                sx={{ fontWeight: 600, color: '#1976d2', mb: 2 }}
                            >
                                Respuesta
                            </Typography>
                            {respuesta ? (
                                <Typography
                                    variant="body1"
                                    sx={{
                                        whiteSpace: 'pre-wrap',
                                        bgcolor: '#fafafa',
                                        p: 2,
                                        borderRadius: 2,
                                        minHeight: 200,
                                    }}
                                >
                                    {respuesta}
                                </Typography>
                            ) : (
                                <Typography
                                    variant="body2"
                                    color="textSecondary"
                                    sx={{
                                        bgcolor: '#fafafa',
                                        p: 2,
                                        borderRadius: 2,
                                        minHeight: 200,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                    }}
                                >
                                    Ingrese una pregunta para obtener una respuesta
                                </Typography>
                            )}
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>
        </Card>
    );
};

export default ConsultaChat;
